import React, { CSSProperties, useContext } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Tooltip } from '@mui/material';
import GameContext from 'contexts/GameContext';
import GlobalContext from 'contexts/GlobalContext';
import { useAppSelector } from 'hooks';
import { useTableSelector } from 'hooks/useTableSelector';
import { useOrientation } from 'hooks/useOrientation';

import { shouldShowAddChips } from './AddChipsButton';
import ExitTableButton from './ExitTableButton';
import SitOutButton from './SitOutButton';

// The cluster is Add Chips (opt-in) → Sit Out → Exit Table. There is no Observe
// (stand-up) control: it was removed from BOTH the single-table view and the
// grid cell, which share this component. A player who ends up observing anyway
// (the engine's LEAVING status, e.g. stood up from another client) still gets
// the felt's "I'm Back" overlay — SitOutOverlay mirrors the seat status itself
// and never depended on this cluster.
//
// `showAddChips` surfaces an Add Chips control at the TOP of the cluster — used
// by the single-table landscape rail so Add Chips groups with Sit Out / Exit
// instead of floating alone in the header. The portrait header renders its own
// Add Chips and omits this; the grid cell uses the footer AddChipsButton.
//
// `showExit` gates the Exit Table button (default true — shown everywhere:
// single-table view keeps a direct Exit control on the table alongside the
// hamburger menu's copy, and grid cells rely on it entirely since a tile has no
// per-cell hamburger). Kept as a prop so a caller can still opt out if needed.
const TableActions = ({
  showAddChips = false,
  showExit = true
}: {
  showAddChips?: boolean;
  showExit?: boolean;
}) => {
  // isSatOut is no longer read here — it only ever gated the removed Observe
  // (stand-up) control; Sit Out gates on isStandingUp, which still matters
  // because a player observing shouldn't be offered a sit-out.
  const { isStandingUp, setOpenAddChips } = useContext(GameContext);
  const { setCloseBuyInModal } = useContext(GlobalContext);

  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const game = useTableSelector((table) => table.game);
  const maximumTopUp = useTableSelector((table) => table.game.maximumTopUp);
  const userBalance = useAppSelector((state) => state.user.playerBankAmount);
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const isWaitingAndExit = useTableSelector(
    (table) => table.currentPlayer.isWaitingAndExit
  );
  // Player is in the "LEFT GAME" rejoining state (status 10) — they have not
  // yet committed to a Rejoin click. Surfacing SitOut here would let
  // them sidestep the explicit rejoin flow and re-enter play in an ambiguous
  // state, so we hide Sit Out until the rejoin completes.
  const isRejoining = useTableSelector((table) => table.currentPlayer.isRejoining);
  const tableType = useTableSelector((table) => table.game.tabletype);
  const { canExitTable } = useTableSelector((table) => table.gameState);
  const playerStatus = game[`p${seatId}status`];
  const orientation = useOrientation();
  const isPortrait = orientation === 'portrait';

  return (
    <div
      style={
        isPortrait
          ? {
              // Saloon stacked cluster — the column shrinks to its widest label
              // ("EXIT TABLE") and every button stretches to that width, so all
              // three are uniform and the cluster stays compact / right-aligned.
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              width: 'fit-content',
              marginLeft: 'auto',
              gap: 4
            }
          : { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.25rem' }
      }>
      {showAddChips &&
        shouldShowAddChips(tableType, isSeated, userBalance, Number(maximumTopUp)) && (
          // Ghost-tooltip fix (matches EN-10433 across ExitTableButton /
          // SitOutButton / portrait Add Chips): icon-only
          // button ("+"), so keep the Tooltip for hover-on-real-pointer
          // discovery but disable the touch + focus listeners so a tap /
          // programmatic focus can't leave a stale "Add Chips" label
          // floating next to the button on mobile.
          <Tooltip title='Add Chips' disableTouchListener disableFocusListener>
            <button
              aria-label='Add chips'
              onClick={() => {
                setOpenAddChips(true);
                setCloseBuyInModal(false);
              }}
              style={addChipsIconBtnStyle}>
              {/* "+" icon (not an "ADD CHIPS" label) so the landscape rail matches
                  the portrait header's Add Chips affordance. */}
              <AddCircleOutlineIcon style={{ fontSize: '1.3rem' }} />
            </button>
          </Tooltip>
        )}
      {canExitTable &&
        !(playerStatus === 5 || playerStatus === 4) &&
        isSeated &&
        !isStandingUp &&
        !isWaitingAndExit &&
        !isRejoining && <SitOutButton />}
      {showExit && <ExitTableButton />}
    </div>
  );
};

export default TableActions;

// Brown "leather" Add Chips "+" button. Exported so the portrait header's Add
// Chips uses the EXACT same treatment (previously it used the faint saloonIconBtn
// and looked different) — the control now reads identically in both orientations.
export const addChipsIconBtnStyle: CSSProperties = {
  alignSelf: 'flex-end',
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(180deg, rgba(74,35,10,0.65), rgba(40,18,6,0.75))',
  border: '1px solid rgba(232,130,31,0.35)',
  borderRadius: 8,
  color: '#f1e3c7',
  padding: 0,
  cursor: 'pointer',
  boxShadow: '0 2px 6px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,180,92,0.1)',
  fontFamily: 'inherit'
};
